import React, { useMemo } from 'react';
import { WHITE } from 'shared/constants/colors';
import useFormatMessage from 'hooks/useFormatMessage';
import { formatDateRange } from 'shared/constants/date';
import { dayjs } from 'shared/utils/date';
import { getFormatter } from 'shared/utils/formatter';
import { getMetricPrecision } from 'shared/utils/metricPrecision';
import { metricSupportsShareOfTotal } from 'shared/utils/metricChange';
import { fraction } from 'shared/constants/math';
import { getDimensionValue } from 'components/DimensionValue';
import { useMatomoGoalNames } from 'hooks/useMatomoGoalNames';
import { useWebsiteCurrency } from 'hooks/useWebsiteCurrency';
import Chart from './Chart';
import { metricsTooltipFormatter } from './tooltip';
import { getRowId } from './utils';

const StackedBarChartResult = ({ results, config, segments, mainSegment, selectedMetric }) => {
  const $t = useFormatMessage();
  const { goalNames, loading: goalNamesLoading } = useMatomoGoalNames();
  const { currency, ready } = useWebsiteCurrency();
  const displayCurrency = ready ? currency : null;

  const metrics = config?.metrics || [];
  const dimensions = config?.dimensions || [];
  const { verticalBars, hideLabels, shareOfTotal } = config?.settings || {};

  const option = useMemo(() => {
    if (!results) {
      return {};
    }
    const metric = metrics.find(m => m.name === selectedMetric);
    const useShareOfTotal = shareOfTotal && metricSupportsShareOfTotal(metric);
    const mainResult = results.find(r => r.segmentId === mainSegment.id);
    const dimensionIds = dimensions.map(d => d.name);
    const dimensionAxis = {
      type: 'category',
      data: segments.map(s => s.id),
      inverse: !verticalBars,
      axisLabel: {
        formatter: (value) => {
          const segment = segments.find(s => s.id === value);
          if (segment) {
            return `{s${segments.indexOf(segment)}|}  ${segment.title} - ${formatDateRange(dayjs, segment.dateRange.period, segment.dateRange.startDate, segment.dateRange.endDate, { short: true, week: 'Week' })}`;
          }
          return value;
        },
        rich: {
          ...segments.reduce((acc, segment, index) => {
            acc[`s${index}`] = {
              color: WHITE,
              backgroundColor: segment.color,
              borderRadius: 4,
              width: 25,
              height: 16,
            };
            return acc;
          }, {}),
        },
        width: 130,
        fontSize: 14,
        overflow: 'truncate',
      },
    };
    const metricAxis = {
      type: 'value',
    };

    return {
      grid: {
        left: 20,
        right: 20,
        top: 40,
        bottom: 20,
        containLabel: true,
      },
      legend: {
        top: 0,
        padding: [10, 20],
        formatter: (value) => {
          const row = mainResult.data.find(d => getRowId(d, dimensionIds) === value);
          return dimensions.map(d => getDimensionValue({ $t, value: row[d.name], dimension: d, goalNames, goalNamesLoading })).join(' - ');
        },
        type: 'scroll',
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => metricsTooltipFormatter({
          $t,
          segments,
          metrics,
          results,
          dimensions,
          rowId: params.seriesName,
          goalNames,
          goalNamesLoading,
          currency: displayCurrency,
        }),
      },
      yAxis: verticalBars ? metricAxis : dimensionAxis,
      xAxis: verticalBars ? dimensionAxis : metricAxis,
      series: mainResult.data.slice(0, 25).map(d => {
        const rowId = getRowId(d, dimensionIds);
        const data = segments.map(s => {
          const result = results.find(r => r.segmentId === s.id);
          const value = result?.data.find(d2 => getRowId(d2, dimensionIds) === rowId)?.[selectedMetric];
          if (useShareOfTotal) {
            return fraction(value, result?.totals[selectedMetric]);
          }
          return value;
        });

        return {
          type: 'bar',
          stack: 'stack',
          stackOrder: 'seriesAsc',
          barMaxWidth: 100,
          emphasis: {
            focus: 'series',
          },
          name: rowId,
          data,
          labelLayout: {
            hideOverlap: true,
          },
          label: hideLabels ? undefined : {
            show: true,
            position: 'inside',
            color: WHITE,
            formatter: (args) => {
              const type = useShareOfTotal ? 'percent' : metric.type || 'number';
              const formatter = getFormatter(type, {
                currency: displayCurrency,
                maximumFractionDigits: getMetricPrecision(metric, 'display'),
              });
              return formatter(args.data);
            },
          },
        };
      }),
    };
  }, [results, dimensions, metrics, mainSegment, goalNames, goalNamesLoading, displayCurrency, $t, verticalBars, hideLabels, shareOfTotal, selectedMetric, segments]);

  return (
    <Chart option={option} height={verticalBars ? 500 : (segments.length * 80) + 40} />
  );
};

export default StackedBarChartResult;
